// @flow
/* eslint-disable flowtype/no-weak-types */
import * as React from 'react';
import { findIndex, flow, get, isEmpty, isEqual } from 'lodash';

import { updateArrayIndexFp } from 'Utils/immutability/immutability';
import { IS_VALID, IS_INVALID, NOT_VALIDATED } from './validationStates';

function _isFormValid(fields: Array<FormField>): boolean {
  return fields.every(field => field.validationState === IS_VALID);
}

function _isFieldValid(field: FormField): boolean {
  const fieldValidators = get(field, 'validators') || [];
  return !isEmpty(fieldValidators)
    ? fieldValidators.every(validator => validator.isValid(field.value))
    : true;
}

function _setFieldValidationState(field: FormField): FormField {
  if (isEmpty(get(field, 'validators'))) {
    return {
      ...field,
      validationState: IS_VALID,
    };
  }
  if (!field.value) {
    return {
      ...field,
      validationState: _isFieldValid(field) ? IS_VALID : NOT_VALIDATED,
    };
  }

  return {
    ...field,
    validationState: _isFieldValid(field) ? IS_VALID : IS_INVALID,
  };
}

type CallBack = (*) => *;

class FormValidator extends React.Component<*, FormValidationState> {
  constructor(props: *) {
    super(props);

    const formFields = this.getFields(this.props) || [];
    this.state = {
      ...this.state,
      fields: formFields.map(field => _setFieldValidationState(field)),
    };

    (this: any).setFieldsInState = this.setFieldsInState.bind(this);
    (this: any).isFormSubmittable = this.isFormSubmittable.bind(this);
    (this: any).isFieldValid = this.isFieldValid.bind(this);
    (this: any).onSubmit = this.onSubmit.bind(this);
    (this: any).updateFieldValue = this.updateFieldValue.bind(this);
    (this: any).validateFields = this.validateFields.bind(this);
    (this: any).getFieldByName = this.getFieldByName.bind(this);
    (this: any).getFieldValueByName = this.getFieldValueByName.bind(this);
  }

  componentDidUpdate(prevProps: *) {
    if (!isEqual(this.props, prevProps)) {
      this.setFieldsInState(this.props);
    }
  }

  setFieldsInState(props: *) {
    const formFields = this.getFields(props) || [];
    this.setState({ fields: formFields.map(field => _setFieldValidationState(field)) });
  }

  /**
   * getFields has to be implemented by the inherited form class
   *
   * @param props (the props used to get/calculate the form fields)
   * @returns the list of form fields
   *
   * const someValidFormField = {
   *    name : a string to eventually identify the field
   *    value : anything
   *    validators : a list of validators to validate the field
   *        -> ex: utils/common-validator/CommonValidators
   * }
   */
  // eslint-disable-next-line no-unused-vars
  getFields(props: *): Array<FormField> {
    return [];
  }

  validateFields(callback?: CallBack): Array<FormField> {
    const fieldsWithValidation = this.state.fields.map(_setFieldValidationState);
    this.setState({ fields: fieldsWithValidation }, callback);
    return fieldsWithValidation;
  }

  updateFieldValue(
    fieldName: string,
    options?: { validate: boolean },
    callback?: CallBack,
  ): (*) => void {
    return newFieldValue => {
      const indexToUpdate = findIndex(this.state.fields, { name: fieldName });
      const newFields = updateArrayIndexFp(this.state.fields, indexToUpdate, field => ({
        ...field,
        value: newFieldValue,
      }));

      this.setState(
        { fields: newFields },
        () => (get(options, 'validate') ? this.validateFields(callback) : callback),
      );
    };
  }

  updateFieldsValue(
    keyValues: Array<{ fieldName: string, value: * }>,
    options?: { validate: boolean },
    callback?: CallBack,
  ) {
    const newFields = keyValues.reduce((updatedFields, { fieldName, value }) => {
      const indexToUpdate = findIndex(updatedFields, { name: fieldName });
      return updateArrayIndexFp(updatedFields, indexToUpdate, field => ({
        ...field,
        name: fieldName,
        value: value,
      }));
    }, this.state.fields);

    this.setState(
      { fields: newFields },
      () => (get(options, 'validate') ? this.validateFields(callback) : callback),
    );
  }

  isFormSubmittable(callback?: CallBack): boolean {
    return flow(
      this.validateFields,
      _isFormValid,
    )(callback);
  }

  isFieldValid(field: FormField): boolean {
    return _isFieldValid(field);
  }

  isFormValid(): boolean {
    return _isFormValid(this.state.fields);
  }

  onSubmit(): ?boolean {
    return this.isFormSubmittable() && this.props.onSubmit(this.state.fields);
  }

  getFieldByName(fieldName: string): ?FormField {
    return get(this.state, 'fields', []).find(field => field.name === fieldName);
  }

  getFieldValueByName(fieldName: string): * {
    const formField = get(this.state, 'fields', []).find(field => field.name === fieldName);
    return get(formField, 'value');
  }
}

export default FormValidator;
